import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Container, Spinner, EmptyState } from "@/components/common";
import { formatDate, formatTime, formatPrice } from "@/lib/format";
import { fileUrl } from "@/components/FileUpload";
import { EmbedHtml } from "@/components/EmbedHtml";
import { MapPin, Clock, Ticket, ExternalLink, Facebook } from "lucide-react";

export default function EventDetail() {
  const { slug } = useParams();
  const { data: e, isLoading, isError } = useQuery({ queryKey: ["event", slug], queryFn: async () => (await api.get(`/events/${slug}`)).data });
  if (isLoading) return <Spinner />;
  if (isError || !e) return <Container className="py-20"><EmptyState title="Event not found" /></Container>;

  return (
    <Container className="py-10">
      <Link to="/events" className="text-sm font-semibold text-[#B04B26] hover:underline">← All events</Link>
      <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {(e.poster_path || e.poster_url) && <img src={e.poster_path ? fileUrl(e.poster_path) : e.poster_url} alt={e.title} className="mb-6 w-full rounded-2xl border border-border object-cover" data-testid="event-poster" />}
          <h1 className="font-display text-3xl font-extrabold text-[#2D1E18] sm:text-4xl">{e.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span data-testid="event-detail-category" className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${e.category === "community" ? "bg-[#EBE1D8] text-[#5C4A42]" : "bg-[#C85A32] text-white"}`}>{e.category === "community" ? "Community social" : "Our event"}</span>
            {e.host_name && <span className="text-sm font-semibold text-[#6E5B51]">Hosted by {e.host_name}</span>}
          </div>
          <p className="mt-6 whitespace-pre-line text-lg leading-relaxed text-[#5C4A42]">{e.description}</p>
          {e.ticketscandy_embed && (
            <div className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="event-tickets-section">
              <h2 className="font-display text-xl font-bold text-[#2D1E18]"><Ticket size={18} className="mr-2 inline text-[#B04B26]" />Buy tickets</h2>
              <EmbedHtml html={e.ticketscandy_embed} className="mt-4" testid="ticketscandy-embed" />
            </div>
          )}
        </div>
        <aside className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-6">
            <p className="flex items-center gap-2 font-semibold text-[#2D1E18]"><Clock size={18} className="text-[#B04B26]" /> {formatDate(e.date, { year: true })}</p>
            {e.start_time && <p className="mt-1 pl-7 text-sm text-[#5C4A42]">{formatTime(e.start_time)}{e.finish_time ? ` – ${formatTime(e.finish_time)}` : ""}</p>}
            {e.venue && <p className="mt-4 flex items-center gap-2 font-semibold text-[#2D1E18]"><MapPin size={18} className="text-[#B04B26]" /> {e.venue.name}</p>}
            {e.venue?.postcode && <p className="pl-7 text-sm text-[#5C4A42]">{e.venue.address}, {e.venue.postcode}</p>}
            {e.price ? <p className="mt-4 flex items-center gap-2 font-bold text-[#B04B26]"><Ticket size={18} /> {formatPrice(e.price)}</p> : null}
            {e.ticket_url && <a href={e.ticket_url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C85A32] px-4 py-3 font-semibold text-white hover:bg-[#B04B26]"><ExternalLink size={16} /> Book tickets</a>}
            {e.facebook_url && <a href={e.facebook_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-border px-4 py-3 font-semibold text-[#5C4A42] hover:border-[#C85A32]"><Facebook size={16} /> Facebook event</a>}
          </div>
        </aside>
      </div>
    </Container>
  );
}
